import { useState } from "react";
import {
  Check,
  CaretDown as ChevronDown,
  Question as HelpCircle,
  Sparkle as Sparkles,
  X,
} from "@phosphor-icons/react";
import { useSiteUser } from "./session";
import { Button } from "@charisol-spark/ui";
import { Badge } from "@charisol-spark/ui";
import { Tooltip } from "@charisol-spark/ui";
import { cn } from "@charisol-spark/ui";
import { creditsCell, planFeaturesWithCredits } from "@charisol-spark/ui";
import { LandingNav } from "./LandingNav";
import { LandingFooter } from "./LandingFooter";
import {
  NUMERIC_COMPARISON_ROWS,
  PLANS,
  type SubscriptionTier,
} from "@charisol-spark/billing";

/* ──────────────────────────────────────────────
   Plan data comes from @charisol-spark/billing, shared with the app's upgrade
   page and with the backend that enforces these ceilings. It was previously a
   local copy, and it had drifted: the persona row below claimed 10 for Pro
   while the API enforced 50 and the app advertised "Unlimited".

   Both the feature bullets and the numeric comparison rows are generated from
   the limits, so this page cannot promise a ceiling the API will refuse.
   ────────────────────────────────────────────── */
type TierKey = SubscriptionTier;

/* ──────────────────────────────────────────
   Feature comparison table data
   ────────────────────────────────────────── */
interface CompRow {
  feature: string;
  hint: string;
  free: string | boolean;
  pro: string | boolean;
  team: string | boolean;
  enterprise: string | boolean;
}

const creditsByTier = Object.fromEntries(
  PLANS.map((p) => [p.key, creditsCell(p)])
) as Record<SubscriptionTier, string>;

const CREDITS_ROW: CompRow = {
  feature: "AI credits / mo",
  hint: "AI credits available each billing month. 1 credit = $0.001 of AI provider cost; a typical post costs about 10 credits.",
  free: creditsByTier.free,
  pro: creditsByTier.pro,
  team: creditsByTier.team,
  enterprise: creditsByTier.enterprise,
};

const COMPARISON: CompRow[] = [
  // Personas and X posts are derived from the shared plan limits rather than
  // typed here -- that is where the drift was. The generations row from
  // NUMERIC_COMPARISON_ROWS is dropped in favour of CREDITS_ROW: the same
  // ceiling, restated in the unit users see everywhere else on this page.
  ...NUMERIC_COMPARISON_ROWS.filter(
    (r) => r.feature !== "AI generations / mo"
  ).map((r) => ({
    feature: r.feature,
    hint: r.hint,
    free: r.cells.free,
    pro: r.cells.pro,
    team: r.cells.team,
    enterprise: r.cells.enterprise,
  })),
  CREDITS_ROW,
  {
    feature: "Platforms",
    hint: "Social networks you can format and publish to. LinkedIn and X today; Facebook, Instagram and Threads are coming soon.",
    // Free was listed as LinkedIn-only while the API let it post to X without
    // limit. It now has a small monthly X allowance (see the X posts row).
    // Only LinkedIn and X exist on any plan, so no tier can say "All".
    free: "LinkedIn, X",
    pro: "LinkedIn, X",
    team: "LinkedIn, X",
    enterprise: "LinkedIn, X",
  },
  {
    feature: "Rich text editor",
    hint: "Full-featured editor with formatting, markdown and live preview.",
    free: true,
    pro: true,
    team: true,
    enterprise: true,
  },
  {
    feature: "Export (TXT, MD)",
    hint: "Download your drafts as plain text or Markdown files.",
    free: true,
    pro: true,
    team: true,
    enterprise: true,
  },
  {
    feature: "Version history",
    hint: "Browse and restore earlier versions of any draft.",
    free: true,
    pro: true,
    team: true,
    enterprise: true,
  },
  {
    feature: "Analytics (coming soon)",
    hint: "Coming soon: post performance dashboards showing impressions, clicks and engagement over time.",
    free: false,
    pro: true,
    team: true,
    enterprise: true,
  },
  {
    feature: "Scheduling & calendar",
    hint: "Queue posts for future publication with a visual calendar.",
    free: true,
    pro: true,
    team: true,
    enterprise: true,
  },
  {
    feature: "Rooms (coming soon)",
    hint: "Coming soon: shared workspaces where team members create and review posts together.",
    free: false,
    pro: "1 room",
    team: "5 rooms",
    enterprise: "Unlimited",
  },
  {
    feature: "Members per room (coming soon)",
    hint: "Coming soon: the maximum number of collaborators allowed inside a single room.",
    free: false,
    pro: "1",
    team: "10",
    enterprise: "Unlimited",
  },
  {
    feature: "SSO & audit logs (coming soon)",
    hint: "Coming soon: single sign-on with your company's account system, and activity audit trails.",
    free: false,
    pro: false,
    team: true,
    enterprise: true,
  },
  {
    feature: "Dedicated support",
    hint: "Priority access to a named support contact with guaranteed response times.",
    free: false,
    pro: false,
    team: false,
    enterprise: true,
  },
];

/* ──────────────────────────────────────────
   FAQ data
   ────────────────────────────────────────── */
// Payments are not live yet: upgrade buttons are disabled, there is no
// payment provider, no trial and no invoicing. Keep these answers in step
// with docs/product/PRODUCT_ROADMAP.md ("Getting paid") when that changes.
const FAQS = [
  {
    q: "Can I upgrade to a paid plan?",
    a: "Not yet. Paid plans aren't available to buy yet, so everyone is on the Free plan for now. The upgrade buttons will switch on when billing launches.",
  },
  {
    q: "Is there a free trial for paid plans?",
    a: "There's no trial yet, because paid plans aren't on sale yet. The Free plan is free to use with no credit card.",
  },
  {
    q: "What payment methods do you accept?",
    a: "We aren't taking payments yet. We'll share the payment options, including invoicing for larger teams, when billing launches.",
  },
  {
    q: "What happens when I run out of credits?",
    a: "You won't be cut off mid-flow. Spark warns you as you get close to your monthly limit, and gives you a small grace allowance of credits to finish what you're working on. Your credits reset at the start of each month.",
  },
];

/* ═══════════════════════════════════════════
   PricingPage Component
   ═══════════════════════════════════════════ */

/**
 * Public pricing page — full version with comparison table and FAQ.
 *
 * Served at /pricing with no auth guard. Mirrors the in-app upgrade
 * page but includes the detailed feature comparison table and FAQ
 * sections for visitors who haven't signed up yet.
 */
export function PricingPage() {
  const [annual, setAnnual] = useState(false);
  const user = useSiteUser();
  const currentTier: TierKey = (user?.subscriptionTier as TierKey) ?? "free";

  return (
    <div className="min-h-screen bg-background text-foreground font-sans antialiased">
      <LandingNav />

      {/* ── Page content ── */}
      <div className="max-w-7xl mx-auto px-6 md:px-8 pt-28 pb-20 relative overflow-hidden">
        {/* ── Background blobs ── */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 -top-40 -z-10 overflow-hidden blur-3xl sm:-top-80"
        >
          <div className="relative left-[calc(50%-11rem)] aspect-1155/678 w-144.5 -translate-x-1/2 rotate-30 bg-linear-to-tr from-primary to-primary-glow opacity-20 sm:left-[calc(50%-30rem)] sm:w-288.75" />
        </div>

        {/* ── Hero ── */}
        <div className="w-full text-center mb-10 animate-fade-in">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            Pick the perfect <span className="gradient-text">plan</span> for you
          </h1>
          <p className="mt-4 text-base leading-7 text-muted-foreground sm:text-lg">
            Start free, with no credit card. Paid plans are coming soon, and the
            Team and Enterprise features they include are still being built.
          </p>
        </div>

        {/* ── Billing toggle ── */}
        <div className="flex justify-center mb-10 animate-fade-in-delay-1">
          <div className="relative flex rounded-full bg-muted p-1 shadow-inner">
            <button
              onClick={() => setAnnual(false)}
              className={cn(
                "relative z-10 rounded-full px-5 py-1.5 text-sm font-medium transition-all",
                !annual
                  ? "bg-card text-primary shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={cn(
                "relative z-10 flex items-center gap-2 rounded-full px-5 py-1.5 text-sm font-medium transition-all",
                annual
                  ? "bg-card text-primary shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Annual
              <Badge variant="default" className="text-[10px] px-1.5 py-0">
                Save 20%
              </Badge>
            </button>
          </div>
        </div>

        {/* ── Pricing cards ── */}
        <div className="isolate grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 animate-fade-in-delay-2">
          {PLANS.map((plan) => {
            const isCurrentPlan = plan.key === currentTier;
            const price = annual ? plan.annualPrice : plan.monthlyPrice;

            return (
              <div
                key={plan.key}
                className={cn(
                  "relative flex flex-col justify-between rounded-2xl p-6 sm:p-7 transition-all duration-300",
                  plan.highlighted
                    ? "ring-2 ring-primary shadow-xl shadow-primary/10 lg:scale-105 lg:z-10"
                    : "ring-1 ring-border hover:shadow-lg hover:-translate-y-1"
                )}
              >
                {/* "Most Popular" badge */}
                {plan.badge && (
                  <div className="absolute -top-3 left-0 right-0 mx-auto w-28 rounded-full bg-primary px-3 py-0.5 text-center text-[11px] font-semibold text-primary-foreground shadow-sm">
                    {plan.badge}
                  </div>
                )}

                <div>
                  <h3 className="text-lg font-semibold">{plan.name}</h3>

                  {/* Price */}
                  <p className="mt-3 flex items-baseline gap-x-1.5">
                    {price !== null ? (
                      <>
                        <span className="text-4xl font-bold tracking-tight">
                          ${price}
                        </span>
                        {price > 0 && (
                          <span className="text-sm font-medium text-muted-foreground">
                            /month
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-4xl font-bold tracking-tight">
                        Custom
                      </span>
                    )}
                  </p>

                  <p className="mt-1.5 text-sm text-muted-foreground">
                    {plan.tagline}
                  </p>

                  {/* Features list */}
                  <ul className="mt-6 space-y-2.5 text-sm">
                    {planFeaturesWithCredits(plan).map((f) => (
                      <li
                        key={f.label}
                        className={cn(
                          "flex items-start gap-2.5",
                          !f.included && "text-muted-foreground"
                        )}
                      >
                        {f.included ? (
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        ) : (
                          <X className="mt-0.5 h-4 w-4 shrink-0" />
                        )}
                        {f.label}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <Tooltip
                  content={
                    isCurrentPlan
                      ? "This is your current plan"
                      : "Billing coming soon"
                  }
                  side="bottom"
                >
                  <div className="mt-7">
                    <Button
                      disabled
                      variant={plan.highlighted ? "default" : "outline"}
                      className={cn("w-full", isCurrentPlan && "opacity-60")}
                      pill
                    >
                      {isCurrentPlan ? (
                        <>
                          <Sparkles className="h-3.5 w-3.5" />
                          Current Plan
                        </>
                      ) : (
                        plan.cta
                      )}
                    </Button>
                  </div>
                </Tooltip>
              </div>
            );
          })}
        </div>

        {/* ── Feature comparison table ── */}
        <section className="mt-20 animate-fade-in-delay-3">
          <div className="w-full text-center mb-10">
            <p className="text-sm font-semibold text-primary">
              Detailed Breakdown
            </p>
            <h2 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">
              Compare features
            </h2>
          </div>

          <div className="overflow-hidden rounded-xl ring-1 ring-border shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-4 font-semibold">Feature</th>
                    <th className="p-4 font-semibold text-center">Free</th>
                    <th className="p-4 font-semibold text-center text-primary">
                      Pro
                    </th>
                    <th className="p-4 font-semibold text-center">Team</th>
                    <th className="p-4 font-semibold text-center">
                      Enterprise
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {COMPARISON.map((row) => (
                    <tr key={row.feature}>
                      <td className="p-4 font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {row.feature}
                          <Tooltip content={row.hint} side="top">
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-foreground transition-colors"
                              aria-label={`What is ${row.feature}?`}
                            >
                              <HelpCircle className="h-3.5 w-3.5" />
                            </button>
                          </Tooltip>
                        </span>
                      </td>
                      {(["free", "pro", "team", "enterprise"] as const).map(
                        (tier) => (
                          <td key={tier} className="p-4 text-center">
                            <CompCell value={row[tier]} />
                          </td>
                        )
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="mt-20 mb-4 animate-fade-in-delay-5">
          <div className="w-full text-center mb-8">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="w-full divide-y divide-border">
            {FAQS.map((faq) => (
              <FaqItem key={faq.q} question={faq.q} answer={faq.a} />
            ))}
          </div>
        </section>
      </div>

      <LandingFooter />
    </div>
  );
}

/* ── Helper: comparison cell renderer ── */
function CompCell({ value }: { value: string | boolean }) {
  if (typeof value === "string") {
    return <span className="text-muted-foreground">{value}</span>;
  }
  return value ? (
    <Check className="mx-auto h-4.5 w-4.5 text-primary" />
  ) : (
    <X className="mx-auto h-4.5 w-4.5 text-muted-foreground" />
  );
}

/* ── Helper: FAQ accordion item ── */
function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="py-5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between text-left font-medium text-foreground"
      >
        <span>{question}</span>
        <ChevronDown
          className={cn(
            "h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200",
            open && "rotate-180"
          )}
        />
      </button>
      {open && (
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {answer}
        </p>
      )}
    </div>
  );
}
